import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdCloudUpload,
  MdRestartAlt,
  MdPersonOutline,
  MdOutlineImage,
} from "react-icons/md";
import styles from "./ModelUploadModal.module.css";
import { useModelUploadModal } from "../../customHook/ModelUploadModal/useModelUploadModal";

const ModelUploadModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  defaultGender = "",
  defaultSType = "",
  modelToEdit = null,
}) => {
  const {
    isUserRole,
    isEditMode,
    hasChanges,
    isFormValid,
    isFormDirty,
    formData,
    errors,
    previewUrls,
    zoomImage,
    setZoomImage,
    formRef,
    handleInputChange,
    handleInputBlur,
    handleImageChange,
    handleRemoveImage,
    handleSubmit,
    handleReset,
    handleCancel,
    handleBackdropClick,
  } = useModelUploadModal({
    isOpen,
    onClose,
    onSubmit,
    defaultGender,
    defaultSType,
    modelToEdit,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (zoomImage) {
          setZoomImage(null);
          return;
        }
        if (!isLoading) {
          handleCancel();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, zoomImage, isLoading, setZoomImage, handleCancel]);

  if (!isOpen || typeof document === "undefined") return null;

  const renderUploadCard = ({ id, title, imageType, file, previewUrl, error }) => {
    const hasImage = Boolean(file || previewUrl);

    return (
      <div className={styles.uploadSlotWrapper} data-field={imageType}>
        <div className={`${styles.uploadCard} ${error ? styles.uploadCardError : ""}`}>
          <span className={styles.uploadCardTitle}>{title}</span>
          <input
            type="file"
            id={id}
            accept=".jpg,.jpeg,.png"
            onChange={(e) => handleImageChange(e, imageType)}
            className={styles.fileInput}
            disabled={isLoading}
          />
          {hasImage ? (
            <div className={styles.previewWrapper}>
              <img
                src={previewUrl}
                alt={`${title} preview`}
                className={styles.previewImg}
                onClick={() => {
                  if (!isLoading) {
                    document.getElementById(id)?.click();
                  }
                }}
                title="Click to choose a new file"
                style={{ cursor: "pointer" }}
              />
              <button
                type="button"
                className={styles.removePreviewBtn}
                onClick={() => handleRemoveImage(imageType)}
                disabled={isLoading}
                title="Remove image"
              >
                <MdClose />
              </button>
              <div className={styles.fileDetail}>
                <span className={styles.fileName}>
                  {file ? `${file.name}` : `Current: ${formData.sName || title}`}
                </span>
                {file && (
                  <span className={styles.fileSize}>
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </span>
                )}
              </div>
            </div>
          ) : (
            <label htmlFor={id} className="w-full flex-1 flex flex-col items-center justify-center p-2 cursor-pointer text-center group">
              <div className="w-9 h-9 rounded-full bg-slate-100 group-hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors mb-1.5 shadow-sm">
                <MdCloudUpload size={20} />
              </div>
              <span className="font-semibold text-slate-700 text-xs leading-tight">Upload image</span>
              <span className="text-[10px] text-slate-400 mt-0.5 leading-tight">JPG, JPEG, PNG • Max 50MB</span>
              {!isUserRole && (
                <span className="text-[9.5px] font-medium text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full mt-1.5 max-w-[95%] truncate leading-tight">
                  {imageType === "front"
                    ? "Format: <name>.jpg"
                    : "Format: <name>_back.jpg"}
                </span>
              )}
            </label>
          )}
        </div>
        {error && <span className={styles.errorMessage}>{error}</span>}
      </div>
    );
  };

  return createPortal(
    <div
      className={styles.modalOverlay}
      onClick={handleBackdropClick}
    >
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {isEditMode ? "Edit Model" : isUserRole ? "Upload Your Photo" : "Add New Model"}
          </h2>
          <button className={styles.closeButton} onClick={handleCancel} disabled={isLoading}>
            <MdClose />
          </button>
        </div>
        <form ref={formRef} onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formFields}>
            <div className={styles.sectionHeader}>
              <MdPersonOutline className={styles.sectionHeaderIcon} />
              <span className={styles.sectionTitle}>
                {isUserRole ? "Photo Details" : "Model Details"}
              </span>
            </div>

            <div className={styles.detailsGrid}>
              <div className={styles.formGroup}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                  <label htmlFor="sName" className={styles.label} style={{ marginBottom: 0 }}>
                    {isUserRole ? "Display Name" : "Name"} <span className={styles.required}>*</span>
                  </label>
                  <span className={styles.charCount}>{formData.sName.length}/100</span>
                </div>
                <input
                  type="text"
                  id="sName"
                  name="sName"
                  value={formData.sName}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder={isUserRole ? "Auto-extracted after upload" : "Enter model name"}
                  maxLength="100"
                  className={`${styles.input} ${errors.sName ? styles.inputError : ""}`}
                  disabled={isLoading || (isUserRole && !isEditMode)}
                />
                {errors.sName && <span className={styles.errorMessage}>{errors.sName}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="gender" className={styles.label}>
                  Gender <span className={styles.required}>*</span>
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={`${styles.select} ${errors.gender ? styles.inputError : ""}`}
                  disabled={isLoading}
                >
                  <option value="">Select Gender</option>
                  <option value="men">Men</option>
                  <option value="women">Women</option>
                </select>
                {errors.gender && <span className={styles.errorMessage}>{errors.gender}</span>}
              </div>

              {!isUserRole && (
                <div className={styles.formGroup}>
                  <label htmlFor="sType" className={styles.label}>
                    Type <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="sType"
                    name="sType"
                    value={formData.sType}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`${styles.select} ${errors.sType ? styles.inputError : ""}`}
                    disabled={isLoading}
                  >
                    <option value="">Select Type</option>
                    <option value="model">Model</option>
                    <option value="user">User</option>
                  </select>
                  {errors.sType && <span className={styles.errorMessage}>{errors.sType}</span>}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-1 mb-2 mt-2">
              <div className={styles.sectionHeader}>
                <MdOutlineImage className={styles.sectionHeaderIcon} />
                <span className={styles.sectionTitle}>Image Uploads</span>
              </div>
              {!isUserRole && (
                <ul className="m-0 pl-4 flex flex-col gap-0.5 list-disc text-[11px] text-slate-500 leading-normal">
                  <li>
                    Allowed formats: <strong className="text-slate-700">JPG, JPEG, PNG</strong> (max 50MB).
                  </li>
                  <li>
                    File names must match model name (<code className="bg-slate-100 px-1 py-0.5 rounded text-primary text-[10.5px] font-semibold">&lt;name&gt;.jpg</code> for front, <code className="bg-slate-100 px-1 py-0.5 rounded text-primary text-[10.5px] font-semibold">&lt;name&gt;_back.jpg</code> for back).
                  </li>
                </ul>
              )}
            </div>

            <div className={`${styles.imageUploadGrid} ${isUserRole ? styles.imageUploadGridSingle : ""}`}>
              {renderUploadCard({
                id: "frontImageModal",
                title: isUserRole ? "Your Photo *" : "Front Image *",
                imageType: "front",
                file: formData.frontImage,
                previewUrl: previewUrls.front,
                error: errors.frontImage,
              })}

              {!isUserRole &&
                renderUploadCard({
                  id: "backImageModal",
                  title: "Back Image (Optional)",
                  imageType: "back",
                  file: formData.backImage,
                  previewUrl: previewUrls.back,
                  error: errors.backImage,
                })}
            </div>
          </div>

          <div className={styles.formActions}>
            {isFormDirty && (
              <button
                type="button"
                className={styles.resetButton}
                onClick={handleReset}
                disabled={isLoading}
                title="Reset form"
              >
                <MdRestartAlt size={14} />
                <span>Reset</span>
              </button>
            )}
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading || !isFormValid || (isEditMode && !hasChanges)}
            >
              {isLoading ? "Saving..."  : (isEditMode ? "Save Changes" : "Save")}
            </button>
          </div>
        </form>
      </div>

      {zoomImage && (
        <div className={styles.zoomOverlay} onClick={() => setZoomImage(null)}>
          <div className={styles.zoomContent} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.zoomCloseBtn}
              onClick={() => setZoomImage(null)}
            >
              <MdClose />
            </button>
            <img src={zoomImage.url} alt={zoomImage.name} className={styles.zoomImg} />
            <span className={styles.zoomName}>{zoomImage.name}</span>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ModelUploadModal;